import dayjs from 'dayjs'
import isBetween from 'dayjs/plugin/isBetween'
import {
  createContext,
  type ReactNode,
  useContext,
  useEffect,
  useState,
} from 'react'

import type { Settings } from '@/lib/api/client/types.gen'
import { subscribeToSettings } from '@/lib/firebase/settings'

dayjs.extend(isBetween)

interface SettingsContextValue {
  settings: Settings | undefined
  isLoading: boolean
  isCheckInOpen: boolean
}

const SettingsContext = createContext<SettingsContextValue>({
  settings: undefined,
  isLoading: true,
  isCheckInOpen: true,
})

export function SettingsProvider({ children }: { children: ReactNode }) {
  const [settings, setSettings] = useState<Settings | undefined>(undefined)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    const unsubscribe = subscribeToSettings((data) => {
      setSettings(data)
      setIsLoading(false)
    })
    return () => unsubscribe()
  }, [])

  const isCheckInOpen = (() => {
    if (!settings?.checkInDateRange) return false
    const { start, end } = settings.checkInDateRange
    const now = dayjs()
    if (start && end) return now.isBetween(dayjs(start), dayjs(end), null, '[]')
    if (start) return now.isAfter(dayjs(start))
    if (end) return now.isBefore(dayjs(end))
    return false
  })()

  return (
    <SettingsContext.Provider value={{ settings, isLoading, isCheckInOpen }}>
      {children}
    </SettingsContext.Provider>
  )
}

export function useSettingsContext() {
  return useContext(SettingsContext)
}
